// 多语言 / 国际化模块
//
// 设计：
//   - 引擎（本文件 tx.lang.js）：纯逻辑，不含翻译数据
//     提供 t / setLang / getLang / registerResources / on / off / loadLocale
//   - 数据 + 初始化（tx.i18n.js）：内置本地 JSON 资源，注册到引擎，检测初始语言
//   - 后续线上 JSON：调 ttx.lang.loadLocale(url, lang) 异步加载并 registerResources
//
// 挂载：window.ttx.lang.{ t, getText, setLang, getLang, registerResources, on, off, loadLocale, currentLang }
//
// 用法：
//   ttx.lang.t('common.ok')                      // → "确定"
//   ttx.lang.t('common.hello', { name: 'Tom' })  // → "你好，Tom"
//   ttx.lang.setLang('en-US')                    // 切换英文，触发 'langchange'
//   ttx.lang.loadLocale('/locales/en-US/biz.json', 'en-US')  // 线上加载
window.ttx = window.ttx || {};
ttx.lang = ttx.lang || {};

(function () {
    // ── 默认值与内部状态 ──────────────────────────────────────────────
    var DEFAULT_LANG = 'zh-CN';
    var STORAGE_KEY = 'ttx-lang';

    // 当前语言
    var _lang = DEFAULT_LANG;

    // 资源池：{ 'zh-CN': { 'common.ok': '确定' }, 'en-US': { ... } }
    var _resources = {};
    _resources[DEFAULT_LANG] = {};

    // 事件监听器
    var _listeners = [];

    // ── 保留原有 UUID 映射（向后兼容） ───────────────────────────────
    ttx.lang._uuidMap = ttx.lang._uuidMap || {};

    ttx.lang.templateByUuid = function (uuid, defaultText) {
        return ttx.lang._uuidMap[uuid] || defaultText || uuid;
    };

    // 原 register(uuid, text) 保留不动，新资源注册用 registerResources
    ttx.lang.register = function (uuid, text) {
        ttx.lang._uuidMap[uuid] = text;
    };

    ttx.lang.registerBatch = function (map) {
        Object.assign(ttx.lang._uuidMap, map);
    };

    // ── i18n 核心 API ────────────────────────────────────────────────

    /**
     * 翻译函数
     * @param {string} key - 翻译键，如 'common.ok'
     * @param {object} [params] - 插值参数，如 { name: 'Tom' }
     * @returns {string} 翻译文本，未命中回退到默认语言，仍不命中返回 key
     */
    ttx.lang.t = function (key, params) {
        if (!key) return '';

        var bundle = _resources[_lang] || {};
        var text = bundle[key];

        // 回退到默认语言
        if (text === undefined && _lang !== DEFAULT_LANG) {
            text = (_resources[DEFAULT_LANG] || {})[key];
        }

        // 仍未命中：返回 key（便于排查缺失项）
        if (text === undefined || text === null) {
            return key;
        }

        // 简单插值：{name} → params.name
        if (params && typeof text === 'string') {
            text = text.replace(/\{(\w+)\}/g, function (_, name) {
                return params[name] !== undefined ? String(params[name]) : '{' + name + '}';
            });
        }

        return text;
    };

    // 语义别名
    ttx.lang.getText = function (key, params) {
        return ttx.lang.t(key, params);
    };

    /**
     * 切换语言
     * @param {string} lang - 语言代码，如 'zh-CN' / 'en-US'
     */
    ttx.lang.setLang = function (lang) {
        if (!lang || lang === _lang) return;

        // 没有资源包也允许切换（可能资源稍后通过 loadLocale 加载）
        if (!_resources[lang]) {
            _resources[lang] = {};
        }

        _lang = lang;

        // 持久化到 localStorage
        try {
            localStorage.setItem(STORAGE_KEY, lang);
        } catch (e) {
            // localStorage 不可用时静默降级
        }

        // 触发 'langchange' 事件，通知订阅的组件重渲染
        // 参照 tx.theme.js 的 accentcolorchange 模式，同时派发 CustomEvent 方便非 React 端监听
        _emit('langchange', { lang: lang });
        try {
            window.dispatchEvent(new CustomEvent('langchange', { detail: { lang: lang } }));
        } catch (e) {
            // CustomEvent 在极少数环境不可用时降级
        }
    };

    /**
     * 获取当前语言代码
     * @returns {string} 如 'zh-CN'
     */
    ttx.lang.getLang = function () {
        return _lang;
    };

    /**
     * 注册语言包（批量合并）
     * @param {object} resources - { 'zh-CN': { 'common.ok': '确定' }, 'en-US': { 'common.ok': 'OK' } }
     *
     * 子应用可在挂载时注册自己的翻译：
     *   ttx.lang.registerResources({ 'zh-CN': { 'biz.title': '业务对象' }, 'en-US': { 'biz.title': 'Business Object' } });
     */
    ttx.lang.registerResources = function (resources) {
        if (!resources || typeof resources !== 'object') return;

        Object.keys(resources).forEach(function (lang) {
            if (!_resources[lang]) {
                _resources[lang] = {};
            }
            var bundle = resources[lang];
            Object.keys(bundle).forEach(function (key) {
                _resources[lang][key] = bundle[key];
            });
        });
    };

    /**
     * 订阅事件
     * @param {string} event - 事件名，目前支持 'langchange'
     * @param {function} cb - 回调，接收 payload
     */
    ttx.lang.on = function (event, cb) {
        if (typeof cb !== 'function') return;
        _listeners.push({ event: event, cb: cb });
    };

    /**
     * 取消订阅
     */
    ttx.lang.off = function (event, cb) {
        _listeners = _listeners.filter(function (l) {
            return !(l.event === event && l.cb === cb);
        });
    };

    /**
     * 异步加载线上 JSON 语言包并注册
     * @param {string} url - JSON 文件地址，如 '/locales/en-US/biz.json'
     * @param {string} lang - 该 JSON 所属语言，如 'en-US'
     * @returns {Promise<object>} 加载完成的资源对象
     *
     * 用法：
     *   ttx.lang.loadLocale('/locales/en-US/biz.json', 'en-US').then(function () {
     *     // 加载完成，ttx.lang.t('biz.xxx') 已可用
     *   });
     */
    ttx.lang.loadLocale = function (url, lang) {
        if (!url || !lang) return Promise.reject(new Error('url and lang required'));

        return fetch(url)
            .then(function (res) {
                if (!res.ok) {
                    throw new Error('loadLocale failed: ' + res.status + ' ' + url);
                }
                return res.json();
            })
            .then(function (data) {
                var pkg = {};
                pkg[lang] = data;
                ttx.lang.registerResources(pkg);
                return data;
            });
    };

    // ── 内部工具 ─────────────────────────────────────────────────────

    function _emit(event, payload) {
        _listeners.forEach(function (l) {
            if (l.event === event) {
                try {
                    l.cb(payload);
                } catch (e) {
                    // 单个监听器异常不影响其他
                    if (typeof console !== 'undefined' && console.warn) {
                        console.warn('[ttx.lang] listener error:', e);
                    }
                }
            }
        });
    }

    // 暴露当前语言的只读引用（动态 getter 保证取到最新值）
    Object.defineProperty(ttx.lang, 'currentLang', {
        get: function () { return _lang; },
        configurable: false,
        enumerable: true
    });

    // 暴露资源池只读引用（调试用）
    Object.defineProperty(ttx.lang, 'resources', {
        get: function () { return _resources; },
        configurable: false,
        enumerable: true
    });
})();
